import React from 'react';
import { StoredDocument } from '../services/indexedDB';
import './CVManager.css';

interface CVManagerProps {
  storedDocuments: StoredDocument[];
  currentDocumentId: string | null;
  currentPage: number;
  totalPages: number;
  onFileUpload: (files: FileList) => void;
  onDocumentSelect: (doc: StoredDocument) => void;
  onDocumentDelete: (doc: StoredDocument, event: React.MouseEvent) => void;
  onPageChange: (page: number) => void;
}

const CVManager: React.FC<CVManagerProps> = ({
  storedDocuments,
  currentDocumentId,
  currentPage,
  totalPages,
  onFileUpload,
  onDocumentSelect,
  onDocumentDelete,
  onPageChange
}) => {
  return (
    <div className="cv-manager">
      {/* Upload Section */}
      <div className="sidebar-section">
        <div className="section-title">
          <span className="material-icons">cloud_upload</span>
          Upload Document
        </div>
        <div 
          className="upload-area" 
          onDrop={(e) => {
            e.preventDefault();
            onFileUpload(e.dataTransfer.files);
          }}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => document.getElementById('file-input')?.click()}
        >
          <div className="upload-icon">
            <span className="material-icons">upload_file</span>
          </div>
          <div className="upload-text">Drop your PDF or DOCX document here</div>
          <button className="upload-button">Choose File</button>
        </div>
        <input
          id="file-input"
          type="file"
          accept=".pdf,.docx"
          style={{ display: 'none' }}
          onChange={(e) => e.target.files && onFileUpload(e.target.files)}
        />
        {/* SharePoint button hidden for now
        <div style={{ marginTop: '16px' }}>
          <button className="btn" style={{ width: '100%', justifyContent: 'center' }}>
            <span className="material-icons">cloud</span>
            Browse SharePoint
          </button>
        </div>
        */}
      </div>

      {/* Document History Section */}
      <div className="sidebar-section">
        <div className="section-title">
          <span className="material-icons">history</span>
          Recent Documents
        </div>
        <div className="document-list">
          {storedDocuments.length > 0 ? (
            <>
              {storedDocuments.map((doc) => (
                <div 
                  key={doc.id} 
                  className="document-item"
                  onClick={() => onDocumentSelect(doc)}
                  style={{
                    position: 'relative', // Added for absolute positioning of delete button
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    background: doc.documentId === currentDocumentId 
                      ? 'linear-gradient(135deg, rgba(79, 172, 254, 0.15) 0%, rgba(0, 123, 255, 0.1) 100%)' 
                      : 'rgba(255, 255, 255, 0.03)',
                    border: doc.documentId === currentDocumentId 
                      ? '1px solid rgba(79, 172, 254, 0.3)' 
                      : '1px solid rgba(255, 255, 255, 0.08)',
                    transition: 'all 0.2s ease',
                    boxShadow: doc.documentId === currentDocumentId 
                      ? '0 2px 8px rgba(79, 172, 254, 0.1)' 
                      : 'none'
                  }}
                  onMouseEnter={(e) => {
                    const deleteBtn = e.currentTarget.querySelector('button') as HTMLElement;
                    if (deleteBtn) deleteBtn.style.opacity = '1';
                    if (doc.documentId !== currentDocumentId) {
                      e.currentTarget.style.setProperty('background', 'rgba(255, 255, 255, 0.5)', 'important');
                      e.currentTarget.style.setProperty('border', '1px solid rgba(200, 200, 200, 0.4)', 'important');
                      e.currentTarget.style.setProperty('box-shadow', '0 2px 6px rgba(0, 0, 0, 0.08)', 'important');
                    }
                  }}
                  onMouseLeave={(e) => {
                    const deleteBtn = e.currentTarget.querySelector('button') as HTMLElement;
                    if (deleteBtn) deleteBtn.style.opacity = '0';
                    if (doc.documentId !== currentDocumentId) {
                      e.currentTarget.style.setProperty('background', 'rgba(255, 255, 255, 0.03)', 'important');
                      e.currentTarget.style.setProperty('border', '1px solid rgba(255, 255, 255, 0.08)', 'important');
                      e.currentTarget.style.setProperty('box-shadow', 'none', 'important');
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', width: '100%' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '6px',
                      background: doc.exportedPDF 
                        ? 'linear-gradient(135deg, #FF6B6B 0%, #FF8787 100%)' 
                        : 'linear-gradient(135deg, #4FACFE 0%, #00B4DB 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <span className="material-icons" style={{ 
                        fontSize: '18px', 
                        color: 'white'
                      }}>
                        {doc.exportedPDF ? 'picture_as_pdf' : 'description'}
                      </span>
                    </div>
                    
                    <div style={{ 
                      flex: 1, 
                      minWidth: 0,
                      marginRight: '28px' // Reserve space for delete button
                    }}>
                      <div style={{ 
                        fontSize: '13px', 
                        fontWeight: '600', 
                        marginBottom: '4px',
                        color: doc.documentId === currentDocumentId ? '#2C3E50' : '#333',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        paddingRight: '4px' // Small padding to avoid text touching the edge
                      }}>
                        {doc.fileName}
                      </div>
                      <div style={{ 
                        fontSize: '11px',
                        color: '#999'
                      }}>
                        {new Date(doc.uploadedAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        gap: '8px', 
                        marginTop: '6px' 
                      }}>
                        {doc.exportedPDF && (
                          <span style={{
                            fontSize: '10px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            background: 'rgba(255, 107, 107, 0.1)',
                            color: '#FF6B6B',
                            fontWeight: '500'
                          }}>
                            <span className="material-icons" style={{ fontSize: '10px', marginRight: '3px', verticalAlign: 'middle' }}>
                              check_circle
                            </span>
                            PDF Exported
                          </span>
                        )}
                        {doc.metadata?.processingStatus === 'processing' && (
                          <span style={{
                            fontSize: '10px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            background: 'rgba(79, 172, 254, 0.1)',
                            color: '#4FACFE',
                            fontWeight: '500'
                          }}>
                            <span className="material-icons spinning" style={{ fontSize: '10px', marginRight: '3px', verticalAlign: 'middle' }}>
                              sync
                            </span>
                            Processing
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDocumentDelete(doc, e);
                      }}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        padding: '4px',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        opacity: 0,
                        transition: 'opacity 0.2s ease',
                        color: '#FF6B6B',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <span className="material-icons" style={{ fontSize: '18px' }}>delete</span>
                    </button>
                  </div>
                </div>
              ))}
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '16px',
                  padding: '8px 0'
                }}>
                  <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: '4px 8px',
                      background: 'transparent',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: currentPage === 1 ? 'rgba(255, 255, 255, 0.3)' : '#fff',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '4px 8px',
                      background: 'transparent',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: currentPage === totalPages ? 'rgba(255, 255, 255, 0.3)' : '#fff',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '24px',
              color: 'rgba(255, 255, 255, 0.5)'
            }}>
              <span className="material-icons" style={{ fontSize: '48px', opacity: 0.3 }}>
                inbox
              </span>
              <p style={{ marginTop: '12px', fontSize: '14px' }}>No documents yet</p>
              <p style={{ fontSize: '12px', opacity: 0.7 }}>Upload a CV to get started</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CVManager;